import React, { useState, useEffect } from 'react';
import astroApi from '../../api/astro';
import metadataApi from '../../api/metadata';

const AstroTablesManagement = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schemas, setSchemas] = useState([]);
  const [tablesBySchema, setTablesBySchema] = useState({});
  const [columnsByTable, setColumnsByTable] = useState({});
  const [formData, setFormData] = useState({
    schema_name: '',
    table_name: '',
    ra_column: '',
    dec_column: '',
    id_column: '',
    important_columns: [],
    plotting_instructions: '', // ADD THIS LINE
    display_name: '',
    description: '',
    documentation_route: ''
  });
  const [message, setMessage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [editingTable, setEditingTable] = useState(null);
  const [isModifying, setIsModifying] = useState(false);
  
  const [columnFilters, setColumnFilters] = useState({
    ra: '',
    dec: '',
    id: '',
    important: ''
  });

  // Load astronomical tables on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch astro tables
        const astroTables = await astroApi.getAstroTables();
        setTables(astroTables);
        
        // Fetch available schemas
        const schemasList = await metadataApi.getSchemas();
        setSchemas(schemasList);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Load tables for a schema when selected
  const loadTablesForSchema = async (schema) => {
    if (tablesBySchema[schema]) return; // Already loaded
    
    try {
      const tables = await metadataApi.getTables(schema);
      setTablesBySchema(prev => ({
        ...prev,
        [schema]: tables
      }));
    } catch (err) {
      console.error(`Failed to load tables for schema ${schema}:`, err);
      setError(`Failed to load tables for schema ${schema}.`);
    }
  };
  
  // Load columns for a table when selected
  const loadColumnsForTable = async (schema, table) => {
    const tableKey = `${schema}.${table}`;
    if (columnsByTable[tableKey]) return; // Already loaded
    
    try {
      const columns = await metadataApi.getColumns(schema, table);
      setColumnsByTable(prev => ({
        ...prev,
        [tableKey]: columns
      }));
    } catch (err) {
      console.error(`Failed to load columns for ${schema}.${table}:`, err);
      setError(`Failed to load columns for ${schema}.${table}.`);
    }
  };

    const resetForm = () => {
    setFormData({
      schema_name: '',
      table_name: '',
      ra_column: '',
      dec_column: '',
      id_column: '',
      important_columns: [],
      plotting_instructions: '',
      display_name: '',
      description: '',
      documentation_route: ''
    });
    setEditingTable(null);
    setIsModifying(false);
  };

  const handleEdit = async (table) => {
    try {
      setLoading(true);
      setError(null);
      
      const tableData = await astroApi.getAstroTable(table.id);
      
      setFormData({
        schema_name: tableData.schema_name,
        table_name: tableData.table_name,
        ra_column: tableData.ra_column,
        dec_column: tableData.dec_column,
        id_column: tableData.id_column,
        important_columns: tableData.important_columns || [],
        plotting_instructions: tableData.plotting_instructions || '',
        display_name: tableData.display_name || '',
        description: tableData.description || '',
        documentation_route: tableData.documentation_route || ''
      });
      
      setEditingTable(tableData);
      setIsModifying(true);
      
      await loadTablesForSchema(tableData.schema_name);
      await loadColumnsForTable(tableData.schema_name, tableData.table_name);
      
    } catch (err) {
      console.error('Error loading table for edit:', err);
      setError(err.response?.data?.detail || 'Failed to load table data for editing');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSchemaChange = async (e) => {
    const schema = e.target.value;
    setFormData(prev => ({
      ...prev,
      schema_name: schema,
      table_name: ''
    }));
    
    if (schema) {
      await loadTablesForSchema(schema);
    }
  };
  
  const handleTableChange = async (e) => {
    const table = e.target.value;
    setFormData(prev => ({
      ...prev,
      table_name: table,
      ra_column: '',
      dec_column: '',
      id_column: '',
      important_columns: []
    }));
    
    if (formData.schema_name && table) {
      await loadColumnsForTable(formData.schema_name, table);
    }
  };
  
  const handleColumnSelectionChange = (e, columnType) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [columnType]: value
    }));
  };
  
  const handleImportantColumnsChange = (e) => {
    const options = e.target.options;
    const selectedValues = [];
    
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedValues.push(options[i].value);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      important_columns: selectedValues
    }));
  };
  
  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleColumnFilterChange = (filterType, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleColumnSelect = (columnType, value) => {
    setFormData(prev => ({
      ...prev,
      [columnType]: value
    }));
    // Clear the filter when a selection is made
    const filterKey = columnType.replace('_column', '');
    setColumnFilters(prev => ({
      ...prev,
      [filterKey]: ''
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.schema_name || !formData.table_name || !formData.ra_column || !formData.dec_column || !formData.id_column) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setMessage(null);
      
      let response;
      if (isModifying && editingTable) {
        response = await astroApi.modifyAstroTable(editingTable.id, formData);
        setTables(prev => prev.map(t => t.id === editingTable.id ? response : t));
        setMessage('Astronomical table updated successfully');
      } else {
        response = await astroApi.addAstroTable(formData);
        setTables(prev => [...prev, response]);
        setMessage('Astronomical table added successfully');
      }
      
      resetForm();
      
    } catch (err) {
      console.error('Error saving astronomical table:', err);
      setError(err.response?.data?.detail || 'An error occurred while saving the table');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDelete = async (schema, table) => {
    try {
      setLoading(true);
      
      await astroApi.deleteAstroTable(schema, table);
      
      // Remove from list
      setTables(prev => prev.filter(t => 
        !(t.schema_name === schema && t.table_name === table)
      ));
      
      setMessage(`Table ${schema}.${table} removed successfully`);
      setDeleteConfirm(null);
      
    } catch (err) {
      console.error('Error deleting table:', err);
      setError(err.response?.data?.detail || 'An error occurred while deleting the table');
    } finally {
      setLoading(false);
    }
  };
  
  // Get current columns for the selected table
  const currentColumns = formData.schema_name && formData.table_name
    ? columnsByTable[`${formData.schema_name}.${formData.table_name}`] || []
    : [];

  // Filter functions for different column types
  const filterColumns = (columns, filter) => {
    if (!filter) return columns;
    return columns.filter(col => 
      col.name.toLowerCase().includes(filter.toLowerCase())
    );
  };

  const filteredRAColumns = filterColumns(currentColumns, columnFilters.ra);
  const filteredDecColumns = filterColumns(currentColumns, columnFilters.dec);
  const filteredIDColumns = filterColumns(currentColumns, columnFilters.id);
  const filteredImportantColumns = filterColumns(currentColumns, columnFilters.important);
    
  return (
  <div className="p-6">
    <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary mb-6">
      Astronomical Tables Management
    </h1>
    
    {message && (
      <div className="p-4 mb-4 text-status-success-light-text dark:text-status-success-dark-text bg-status-success-light-bg dark:bg-status-success-dark-bg rounded-md border border-status-success-light-text/20 dark:border-status-success-dark-text/20">
        <span>{message}</span>
        <button 
          onClick={() => setMessage(null)}
          className="float-right font-bold text-status-success-light-text dark:text-status-success-dark-text hover:text-status-success-light-text/80 dark:hover:text-status-success-dark-text/80 transition-colors"
        >
          &times;
        </button>
      </div>
    )}
    
    {error && (
      <div className="p-4 mb-4 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md border border-status-error-light-text/20 dark:border-status-error-dark-text/20">
        <strong className="font-bold">Error: </strong>
        <span>{error}</span>
        <button 
          onClick={() => setError(null)}
          className="float-right font-bold text-status-error-light-text dark:text-status-error-dark-text hover:text-status-error-light-text/80 dark:hover:text-status-error-dark-text/80 transition-colors"
        >
          &times;
        </button>
      </div>
    )}
    
    {/* Current tables section */}
    <div className="bg-ui-light-card dark:bg-ui-dark-card shadow-md rounded-lg p-6 mb-6">
      <h2 className="text-xl font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-4">
        Current Astronomical Tables
      </h2>
      
      {loading && tables.length === 0 ? (
        <div className="flex justify-center py-4">
          <svg className="animate-spin h-6 w-6 text-primary-600 dark:text-primary-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : tables.length === 0 ? (
        <div className="py-4 text-center text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
          No astronomical tables configured yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
            <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Schema
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Table
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Display Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  RA Column
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Dec Column
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  ID Column
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Important Columns
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Plotting Instructions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
              {tables.map((table, index) => (
                <tr key={index} className="hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                    {table.schema_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                    {table.table_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                    {table.display_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                    {table.ra_column}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                    {table.dec_column}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                    {table.id_column}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                    {table.important_columns?.join(', ') || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary max-w-xs">
                    <div className="truncate" title={table.plotting_instructions}>
                      {table.plotting_instructions || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => handleEdit(table)}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                        disabled={loading}
                      >
                        Edit
                      </button>
                      {deleteConfirm === `${table.schema_name}.${table.table_name}` ? (
                        <>
                          <button 
                            onClick={() => handleDelete(table.schema_name, table.table_name)}
                            className="text-status-error-light-text dark:text-status-error-dark-text hover:text-status-error-light-text/80 dark:hover:text-status-error-dark-text/80 transition-colors"
                            disabled={loading}
                          >
                            Confirm
                          </button>
                          <button 
                            onClick={() => setDeleteConfirm(null)}
                            className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-ui-light-text-secondary dark:hover:text-ui-dark-text-secondary transition-colors"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button 
                          onClick={() => setDeleteConfirm(`${table.schema_name}.${table.table_name}`)}
                          className="text-status-error-light-text dark:text-status-error-dark-text hover:text-status-error-light-text/80 dark:hover:text-status-error-dark-text/80 transition-colors"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    
    {/* Add/Edit table form */}
    <div className="bg-ui-light-card dark:bg-ui-dark-card shadow-md rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
          {isModifying ? 'Edit Astronomical Table' : 'Add New Astronomical Table'}
        </h2>
        {isModifying && (
          <button
            onClick={resetForm}
            className="px-3 py-1 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
          >
            Cancel Edit
          </button>
        )}
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Schema selection */}
          <div>
            <label className="block text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-sm font-medium mb-2">
              Schema: <span className="text-status-error-light-text dark:text-status-error-dark-text">*</span>
            </label>
            <select
              value={formData.schema_name}
              onChange={handleSchemaChange}
              className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
              disabled={loading}
              required
            >
              <option value="">Select Schema</option>
              {schemas.map(schema => (
                <option key={schema} value={schema}>{schema}</option>
              ))}
            </select>
          </div>
          
          {/* Table selection */}
          <div>
            <label className="block text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-sm font-medium mb-2">
              Table: <span className="text-status-error-light-text dark:text-status-error-dark-text">*</span>
            </label>
            <select
              value={formData.table_name}
              onChange={handleTableChange}
              className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
              disabled={loading || !formData.schema_name}
              required
            >
              <option value="">Select Table</option>
              {formData.schema_name && tablesBySchema[formData.schema_name]?.map(table => (
                <option key={table} value={table}>{table}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* RA column selection */}
          <div>
            <label className="block text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-sm font-medium mb-2">
              RA Column: <span className="text-status-error-light-text dark:text-status-error-dark-text">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.ra_column}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, ra_column: e.target.value }));
                  handleColumnFilterChange('ra', e.target.value);
                }}
                className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
                disabled={loading || !formData.schema_name || !formData.table_name}
                placeholder="Type to search RA columns..."
                required
              />
              {columnFilters.ra && filteredRAColumns.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md shadow-lg max-h-40 overflow-y-auto">
                  {filteredRAColumns.slice(0, 10).map(col => (
                    <button
                      key={col.name}
                      type="button"
                      onClick={() => handleColumnSelect('ra_column', col.name)}
                      className="w-full px-3 py-2 text-left text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                    >
                      {col.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Dec column selection */}
          <div>
            <label className="block text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-sm font-medium mb-2">
              Dec Column: <span className="text-status-error-light-text dark:text-status-error-dark-text">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.dec_column}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, dec_column: e.target.value }));
                  handleColumnFilterChange('dec', e.target.value);
                }}
                className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
                disabled={loading || !formData.schema_name || !formData.table_name}
                placeholder="Type to search Dec columns..."
                required
              />
              {columnFilters.dec && filteredDecColumns.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md shadow-lg max-h-40 overflow-y-auto">
                  {filteredDecColumns.slice(0, 10).map(col => (
                    <button
                      key={col.name}
                      type="button"
                      onClick={() => handleColumnSelect('dec_column', col.name)}
                      className="w-full px-3 py-2 text-left text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                    >
                      {col.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* ID column selection */}
          <div>
            <label className="block text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-sm font-medium mb-2">
              ID Column: <span className="text-status-error-light-text dark:text-status-error-dark-text">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.id_column}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, id_column: e.target.value }));
                  handleColumnFilterChange('id', e.target.value);
                }}
                className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
                disabled={loading || !formData.schema_name || !formData.table_name}
                placeholder="Type to search ID columns..."
                required
              />
              {columnFilters.id && filteredIDColumns.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md shadow-lg max-h-40 overflow-y-auto">
                  {filteredIDColumns.slice(0, 10).map(col => (
                    <button
                      key={col.name}
                      type="button"
                      onClick={() => handleColumnSelect('id_column', col.name)}
                      className="w-full px-3 py-2 text-left text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                    >
                      {col.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted mt-1">Primary key or unique identifier for objects</p>
          </div>
          </div>
        
        {/* Important columns multi-select */}
        <div className="mb-4">
          <label className="block text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-sm font-medium mb-2">
            Important Columns:
          </label>
          
          {/* Filter input */}
          <input
            type="text"
            value={columnFilters.important}
            onChange={(e) => handleColumnFilterChange('important', e.target.value)}
            className="w-full px-3 py-2 mb-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
            disabled={loading || !formData.schema_name || !formData.table_name}
            placeholder="Filter columns..."
          />
          
          {/* Checkboxes for filtered columns */}
          <div className="max-h-40 overflow-y-auto border border-ui-light-border dark:border-ui-dark-border rounded-md p-2 bg-ui-light-background dark:bg-ui-dark-background">
            {filteredImportantColumns.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {filteredImportantColumns.map(col => (
                  <label key={col.name} className="flex items-center space-x-2 p-1 rounded hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.important_columns.includes(col.name)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            important_columns: [...prev.important_columns, col.name]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            important_columns: prev.important_columns.filter(c => c !== col.name)
                          }));
                        }
                      }}
                      className="rounded text-primary-600 dark:text-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400"
                    />
                    <span className="text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary">{col.name}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary text-center py-2">
                {columnFilters.important ? 'No columns match the filter' : 'Select a table to see columns'}
              </p>
            )}
          </div>
          
          {/* Selected columns summary */}
          {formData.important_columns.length > 0 && (
            <div className="mt-2 p-2 bg-ui-light-hover dark:bg-ui-dark-hover rounded-md">
              <p className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
                Selected columns ({formData.important_columns.length}):
              </p>
              <div className="flex flex-wrap gap-1">
                {formData.important_columns.map(col => (
                  <span key={col} className="inline-flex items-center px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded">
                    {col}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          important_columns: prev.important_columns.filter(c => c !== col)
                        }));
                      }}
                      className="ml-1 text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-200"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary mt-1">
            These columns will be displayed in the detail view chart
          </p>
        </div>
        
        {/* Plotting Instructions */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-sm font-medium">
              Plotting Instructions:
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const example = {
                    "plots": [{
                      "type": "scatter",
                      "title": "SPLUS Photometry",
                      "xAxis": {
                        "type": "fixed",
                        "label": "Wavelength (Å)",
                        "values": [3485, 3785, 3950, 4100, 4300, 4803, 5308, 6250, 6600, 7660, 8610, 9110]
                      },
                      "yAxis": {
                        "type": "pattern",
                        "label": "Magnitude",
                        "pattern": "{band}_auto",
                        "bands": ["u", "F378", "F395", "F410", "F430", "g", "F515", "r", "F660", "i", "F861", "z"]
                      },
                      "options": {
                        "connectPoints": true,
                        "showLegend": true,
                        "invertYAxis": true
                      }
                    }]
                  };
                  handleTextChange({
                    target: {
                      name: 'plotting_instructions',
                      value: JSON.stringify(example, null, 2)
                    }
                  });
                }}
                className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
              >
                Band Photometry
              </button>
              <button
                type="button"
                onClick={() => {
                  const example = {
                    "plots": [{
                      "type": "line",
                      "title": "Spectrum",
                      "xAxis": {
                        "type": "array",
                        "column": "wave",
                        "label": "Wavelength (Å)"
                      },
                      "yAxis": {
                        "type": "array",
                        "column": "flux_b",
                        "label": "Flux"
                      },
                      "options": {
                        "connectPoints": true,
                        "showLegend": false,
                        "invertYAxis": false
                      }
                    }]
                  };
                  handleTextChange({
                    target: {
                      name: 'plotting_instructions',
                      value: JSON.stringify(example, null, 2)
                    }
                  });
                }}
                className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium"
              >
                Array Spectrum
              </button>
            </div>
          </div>
          <textarea
            name="plotting_instructions"
            value={formData.plotting_instructions}
            onChange={handleTextChange}
            rows={12}
            className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50 resize-vertical font-mono text-sm"
            disabled={loading}
            placeholder="Enter JSON plot instructions (see example below)"
          />

          {/* Copyable Example */}
          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-300 dark:border-gray-700 rounded-md">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">Multi-Plot Example (Band Photometry + Spectrum):</p>
              <button
                type="button"
                onClick={() => {
                  const exampleText = `{
  "plots": [
    {
      "type": "scatter",
      "title": "SPLUS Photometry",
      "xAxis": {
        "type": "fixed",
        "label": "Wavelength (Å)",
        "values": [3485, 3785, 3950, 4100, 4300, 4803, 5308, 6250, 6600, 7660, 8610, 9110]
      },
      "yAxis": {
        "type": "pattern",
        "label": "Magnitude",
        "pattern": "{band}_auto",
        "bands": ["u", "F378", "F395", "F410", "F430", "g", "F515", "r", "F660", "i", "F861", "z"]
      },
      "options": {
        "connectPoints": true,
        "showLegend": true,
        "invertYAxis": true
      }
    },
    {
      "type": "line",
      "title": "Spectrum",
      "xAxis": {
        "type": "array",
        "column": "wave",
        "label": "Wavelength (Å)"
      },
      "yAxis": {
        "type": "array",
        "column": "flux_b",
        "label": "Flux"
      },
      "options": {
        "connectPoints": true,
        "showLegend": false,
        "invertYAxis": false
      }
    }
  ]
}`;
                  navigator.clipboard.writeText(exampleText);
                }}
                className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
              >
                Copy to Clipboard
              </button>
            </div>
            <pre className="text-xs text-gray-800 dark:text-gray-200 overflow-x-auto bg-white dark:bg-gray-950 p-2 rounded border border-gray-200 dark:border-gray-800 max-h-96">
{`{
  "plots": [
    {
      "type": "scatter",
      "title": "SPLUS Photometry",
      "xAxis": {
        "type": "fixed",
        "label": "Wavelength (Å)",
        "values": [3485, 3785, 3950, 4100, 4300, 4803, 5308, 6250, 6600, 7660, 8610, 9110]
      },
      "yAxis": {
        "type": "pattern",
        "label": "Magnitude",
        "pattern": "{band}_auto",
        "bands": ["u", "F378", "F395", "F410", "F430", "g", "F515", "r", "F660", "i", "F861", "z"]
      },
      "options": {
        "connectPoints": true,
        "showLegend": true,
        "invertYAxis": true
      }
    },
    {
      "type": "line",
      "title": "Spectrum",
      "xAxis": {
        "type": "array",
        "column": "wave",
        "label": "Wavelength (Å)"
      },
      "yAxis": {
        "type": "array",
        "column": "flux_b",
        "label": "Flux"
      },
      "options": {
        "connectPoints": true,
        "showLegend": false,
        "invertYAxis": false
      }
    }
  ]
}`}</pre>
          </div>

          {/* Schema Documentation */}
          <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
            <p className="text-sm text-blue-800 dark:text-blue-300 font-medium mb-1">JSON Schema Format:</p>
            <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-1 ml-4 list-disc">
              <li><strong>plots</strong>: Array of plot definitions (can have multiple plots per table)</li>
              <li><strong>type</strong>: "scatter", "line", or "bar"</li>
              <li><strong>xAxis/yAxis type</strong>:
                <ul className="ml-4 mt-1 space-y-0.5">
                  <li><strong>fixed</strong>: Hardcoded values (e.g., wavelengths)</li>
                  <li><strong>pattern</strong>: Expand column patterns like column_&#123;band&#125;</li>
                  <li><strong>column</strong>: Single column value</li>
                  <li><strong>array</strong>: Parse array column (handles strings like "[1,2,3]" or JSON arrays)</li>
                </ul>
              </li>
              <li><strong>pattern</strong>: Use &#123;band&#125; placeholder that gets replaced with each band identifier</li>
              <li><strong>options</strong>: connectPoints (boolean), showLegend (boolean), invertYAxis (boolean for magnitudes)</li>
            </ul>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Display name */}
          <div>
            <label className="block text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-sm font-medium mb-2">
              Display Name:
            </label>
            <input
              type="text"
              name="display_name"
              value={formData.display_name}
              onChange={handleTextChange}
              className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
              disabled={loading}
              placeholder="Friendly name for the table"
            />
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-sm font-medium mb-2">
              Description:
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleTextChange}
              className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
              disabled={loading}
              placeholder="Brief description of the data"
            />
          </div>

          {/* Documentation Route */}
          <div>
            <label className="block text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-sm font-medium mb-2">
              Documentation Route:
            </label>
            <input
              type="text"
              name="documentation_route"
              value={formData.documentation_route}
              onChange={handleTextChange}
              className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
              disabled={loading}
              placeholder="Path to documentation (e.g., /docs/catalogs/gaia)"
            />
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white dark:text-ui-dark-text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </div>
            ) : (
              isModifying ? 'Update Table' : 'Add Table'
            )}
          </button>
        </div>
      </form>
    </div>
  </div>
);
};

export default AstroTablesManagement;